import {
  X,
  Plus,
  CreditCard,
  Printer,
  Trash2,
  Clock,
  QrCode,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from '../ui/notify';
import { base_url } from '../../api/index';
import { printReceiptThermal } from '../../api/pos/receiptPrint';
import { usePosOrderRealtime } from '../../hook/usePosOrderRealtime';
import { useCanTakePayment } from '../../hook/useCanTakePayment';
import { useConfirm } from '../ConfirmDialog';
import { useI18n } from '../../i18n/I18nContext';

/**
 * Yeni "Dynamic Island" tarzı sağ aksiyon paneli.
 *
 * Mevcut props sözleşmesi korundu:
 *   table, onClose, onOpenPOS, onShowDetail
 * Veri akışı: tablo seçilince GET /tables/{id}/order çağrılır
 *   (response.data.table.orders[] → stocks[] / sets[] / stock_sets[]).
 * Eski table.orders fallback'i korundu, yeni gerçek veri öncelikli.
 */
interface TableRightDockProps {
  table: any;
  onClose: () => void;
  onOpenPOS: () => void;
  onShowDetail: (table: any) => void;
  onOpenPayment?: () => void;
}

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

interface FlatItem {
  name: string;
  note?: string;
  qty: number;
  price: number; // unit price
}

// Backend siparişleri farklı şekillerde dönebilir — order.stocks /
// order.sets / order.stock_sets / direkt array hepsini destekliyoruz.
const flattenOrderItems = (orders: any[] | undefined): FlatItem[] => {
  if (!Array.isArray(orders)) return [];
  const out: FlatItem[] = [];
  for (const o of orders) {
    if (!o) continue;

    // 1) Düz şekil (zaten flatten edilmiş ürün): name/quantity/price doğrudan
    if (
      !Array.isArray(o.stocks) &&
      !Array.isArray(o.sets) &&
      !Array.isArray(o.stock_sets) &&
      (typeof o.name === 'string' || typeof o.price !== 'undefined' || typeof o.quantity !== 'undefined')
    ) {
      out.push({
        name: String(o.name || 'Ürün'),
        note: typeof o.note === 'string' ? o.note : undefined,
        qty: Number(o.quantity) || 1,
        price: Number(o.price) || 0,
      });
      continue;
    }

    // 2) Sarmalanmış sipariş yapısı: order.stocks[]
    if (Array.isArray(o.stocks)) {
      for (const s of o.stocks) {
        const piv = s?.pivot || {};
        const qty = Number(piv.quantity) || Number(s?.quantity) || 1;
        // unit price önceliği: stock.price → pivot.unit_price
        const unit = Number(s?.price) || Number(piv.unit_price) || 0;
        // detail.unit/count varsa note olarak göster
        const detail = s?.detail;
        const detailNote =
          detail && (detail.count || detail.unit)
            ? `${detail.count ?? ''}${detail.unit ? ` ${detail.unit}` : ''}`.trim()
            : undefined;
        out.push({
          name: String(s?.name || 'Ürün'),
          note: typeof piv.note === 'string' ? piv.note : typeof s?.note === 'string' ? s.note : detailNote,
          qty,
          price: unit,
        });
      }
    }

    // 3) order.sets[] — PosTablePage.tsx'in kullandığı isim
    if (Array.isArray(o.sets)) {
      for (const ss of o.sets) {
        const piv = ss?.pivot || {};
        const qty = Number(piv.quantity) || Number(ss?.quantity) || 1;
        const unit = Number(ss?.price) || Number(ss?.unit_price) || Number(piv.unit_price) || 0;
        out.push({
          name: String(ss?.name || 'Set'),
          note: typeof piv.note === 'string' ? piv.note : 'Set',
          qty,
          price: unit,
        });
      }
    }

    // 4) order.stock_sets[] — bazı backend'lerde alternatif isim
    if (Array.isArray(o.stock_sets)) {
      for (const ss of o.stock_sets) {
        const piv = ss?.pivot || {};
        const qty = Number(piv.quantity) || Number(ss?.quantity) || 1;
        const unit = Number(ss?.unit_price ?? ss?.price) || 0;
        out.push({
          name: String(ss?.name || 'Set'),
          note: typeof piv.note === 'string' ? piv.note : 'Set',
          qty,
          price: unit,
        });
      }
    }
  }
  return out;
};

export function TableRightDock({ table, onClose, onOpenPOS, onShowDetail, onOpenPayment }: TableRightDockProps) {
  // Ofisiantda ödəniş icazəsi yoxdur — "Ödəniş" düyməsi ona görünmür.
  const canTakePayment = useCanTakePayment();
  const { confirm, confirmDialog } = useConfirm();
  const { t } = useI18n();
  const [fullOrders, setFullOrders] = useState<any[]>([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [apiTotalPrice, setApiTotalPrice] = useState<number | null>(null);

  // "Adisyon" → POS rejimində qəbzi termal çek aparatından çap et (brauzersiz).
  const handlePrintReceipt = async () => {
    try {
      const orderId = fullOrders?.[0]?.order_id ?? fullOrders?.[0]?.id;
      if (await printReceiptThermal(orderId)) {
        toast.success(t('dock.receiptSent'));
      }
      // cloud rejimi (false) → köhnə davranış (no-op).
    } catch (e: any) {
      toast.error(e?.message || t('dock.receiptFailed'));
    }
  };

  // "Temizle" → masanı təmizlə: açıq sifariş varsa ləğv et, "dirty" masanı azad et.
  const handleClearTable = async () => {
    const ok = await confirm({
      message: t('dock.clearConfirm'),
      confirmLabel: t('dock.clear'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${base_url}/tables/${table.id}/cancel-order`, getHeaders());
      toast.success(t('dock.tableCleared'));
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('dock.tableClearFailed'));
    }
  };

  // Yalnız ƏN SON sorğunun nəticəsi state-ə yazılsın (masa dəyişimi + real-time
  // refetch üst-üstə düşəndə köhnə cavab yeni masanın adisyonunu əzməsin).
  const reqIdRef = useRef(0);

  const loadOrders = useCallback(
    async (opts?: { silent?: boolean }) => {
      const tableId = table?.id;
      const myReq = ++reqIdRef.current;
      if (!tableId) {
        setFullOrders([]);
        setApiTotalPrice(null);
        return;
      }
      try {
        // Real-time refetch-də spinner göstərmirik — panel "sıçramasın".
        if (!opts?.silent) setOrderLoading(true);
        const res = await axios.get(`${base_url}/tables/${tableId}/order`, getHeaders());
        if (myReq !== reqIdRef.current) return;
        const ordersArr = Array.isArray(res?.data?.table?.orders) ? res.data.table.orders : [];
        setFullOrders(ordersArr);
        // Backend total_price tek order üstünde dönüyor (en yenisi)
        const tp =
          Number(res?.data?.table?.total_price) ||
          Number(ordersArr?.[0]?.total_price) ||
          null;
        setApiTotalPrice(Number.isFinite(tp as number) ? (tp as number) : null);
      } catch (err: any) {
        if (myReq === reqIdRef.current) {
          // 404 / 403 → sipariş yok, sessiz geç
          setFullOrders([]);
          setApiTotalPrice(null);
        }
      } finally {
        if (myReq === reqIdRef.current && !opts?.silent) setOrderLoading(false);
      }
    },
    [table?.id],
  );

  // Tablo değiştiğinde tam sipariş listesini çek
  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  // Real-time: başqa cihazdan (yaxud QR-dan) məhsul əlavə/silinəndə adisyon
  // özü yenilənir — əl ilə refresh lazım deyil.
  usePosOrderRealtime(
    useCallback(() => {
      void loadOrders({ silent: true });
    }, [loadOrders]),
  );

  if (!table) return null;

  // Önce API'den gelen tam orders, fallback olarak Masalar mapping orders
  const sourceOrders = fullOrders.length > 0 ? fullOrders : Array.isArray(table.orders) ? table.orders : [];
  const items = flattenOrderItems(sourceOrders);

  // QEYD: burada əvvəllər `const kdv = subtotal * 0.1` vardı — UI-da uydurulmuş,
  // heç bir yerdən gəlməyən 10% ƏDV (panel ₼43 göstərirdi, kassir ₼39 alırdı).
  // Silindi. İndi hesab haqları admin panelindən gəlir, pos_local `Order.totals()`-da
  // hesablanır və `charges[]` kimi ötürülür — UI heç nə uydurmur, yalnız göstərir.
  const firstOrder = (sourceOrders[0] ?? {}) as {
    charges?: Array<{ id: string; name: string; amount: number }>;
    subtotal_price?: number;
  };
  const charges = Array.isArray(firstOrder.charges) ? firstOrder.charges : [];
  const chargeTotal = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const computedFromItems = items.reduce((s, it) => s + it.qty * it.price, 0);

  // `apiTotalPrice` pos_local-un YEKUN məbləğidir — haqları ONSUZ DA daxil edir.
  // Ona görə ondan haqları çıxarıb ara cəmi alırıq (ikiqat saymamaq üçün).
  const grandTotal =
    apiTotalPrice !== null && apiTotalPrice > 0
      ? apiTotalPrice
      : computedFromItems > 0
        ? computedFromItems + chargeTotal
        : (Number(table.total) || 0) + chargeTotal;

  const subtotal =
    typeof firstOrder.subtotal_price === 'number'
      ? firstOrder.subtotal_price
      : grandTotal - chargeTotal;

  const isEmpty = table.status === 'empty';

  /** Pul formatı — qəpik itməsin (əvvəl maximumFractionDigits:0 ₼39.50-ni ₼40 edirdi). */
  const money = (v: number) =>
    v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.7 }}
      className="pos-card fixed top-20 right-0 bottom-0 w-96 bg-white dark:bg-[#121214] shadow-[-20px_0_60px_-15px_rgba(0,0,0,0.12)] dark:shadow-[-20px_0_60px_-15px_rgba(0,0,0,0.5)] border-l border-gray-200/70 dark:border-white/[0.08] z-40 flex flex-col overflow-hidden"
    >
      {/* Header — Dynamic Island stili */}
      <div className="relative p-6 pb-4">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-10"
        >
          <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>

        <div className="inline-flex items-center gap-3 px-4 py-2 bg-black dark:bg-[#1a1a1a] rounded-full text-white shadow-lg shadow-black/10 dark:shadow-white/5 mb-6 relative overflow-hidden border border-transparent dark:border-white/5">
          <div
            className={`w-2 h-2 rounded-full ${
              table.status === 'occupied'
                ? 'bg-[#ff8a00] shadow-[0_0_12px_rgba(255,138,0,0.8)] animate-pulse'
                : 'bg-gray-400'
            }`}
          />
          <span className="font-semibold text-sm tracking-tight text-white">
            {t('common.table')} {table.number ?? table.displayName ?? table.id}
          </span>

          {table.duration && (
            <>
              <div className="w-px h-3 bg-white/20" />
              <div className="flex items-center gap-1.5 opacity-80 text-white">
                <Clock className="w-3 h-3" />
                <span className="text-xs font-medium">{table.duration}</span>
              </div>
            </>
          )}
        </div>

        {!isEmpty && (
          <div className="flex items-center gap-2 flex-wrap">
            {table.waiter && (
              <>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-white/10 dark:to-white/5 flex items-center justify-center text-[10px] font-semibold text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-white/5">
                  {String(table.waiter).charAt(0)}
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {table.waiter}
                </span>
              </>
            )}
            {table.waiter && table.guests ? (
              <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-700 mx-1" />
            ) : null}
            {table.guests && (
              <span className="text-xs text-gray-500">{t('dock.guests', { n: table.guests })}</span>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 scrollbar-hide pos-scroll">
        {isEmpty ? (
          <div className="h-full flex flex-col items-center justify-center space-y-6 opacity-90">
            <div className="relative">
              <div className="absolute inset-0 bg-[#ff8a00]/20 rounded-full blur-xl" />
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff8a00]/10 to-[#ff8a00]/5 border border-[#ff8a00]/20 flex items-center justify-center relative">
                <Plus className="w-7 h-7 text-[#ff8a00]" />
              </div>
            </div>
            <div className="text-center space-y-1">
              <p className="font-semibold text-gray-900 dark:text-white">{t('dock.tableReady')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('dock.startHint')}
              </p>
            </div>
            <button
              onClick={onOpenPOS}
              className="px-8 py-3 bg-[#ff8a00] hover:bg-[#e07a00] text-white rounded-xl font-semibold shadow-[0_8px_20px_-6px_rgba(255,138,0,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              {t('dock.startOrder')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Adisyon listesi */}
            <div className="space-y-1">
              <div className="flex items-center justify-between px-2 mb-3">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('dock.bill')}
                </span>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {orderLoading && items.length === 0 ? `${t('common.loading')}...` : t('dock.items', { n: items.length })}
                </span>
              </div>

              {orderLoading && items.length === 0 ? (
                // Skeleton — API çağrısı sürerken
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="flex items-start justify-between p-3 rounded-2xl border border-transparent"
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-6 h-6 rounded bg-gray-200/70 dark:bg-white/10 animate-pulse shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3.5 w-3/4 bg-gray-200/70 dark:bg-white/10 rounded animate-pulse" />
                          <div className="h-2.5 w-1/3 bg-gray-200/50 dark:bg-white/5 rounded animate-pulse" />
                        </div>
                      </div>
                      <div className="h-3.5 w-12 bg-gray-200/70 dark:bg-white/10 rounded animate-pulse shrink-0 ml-3" />
                    </div>
                  ))}
                </div>
              ) : items.length === 0 ? (
                <p className="text-xs text-gray-400 px-2 py-4 text-center">
                  {t('dock.noItems')}
                </p>
              ) : (
                items.map((item, i) => (
                  <div
                    key={i}
                    className="group flex items-start justify-between p-3 rounded-2xl hover:bg-gray-50 dark:hover:bg-white/5 transition-all cursor-pointer border border-transparent hover:border-gray-100 dark:hover:border-white/5"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-6 h-6 rounded bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white flex items-center justify-center font-semibold text-xs border border-gray-200/50 dark:border-white/5 shrink-0">
                        {item.qty}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {item.name}
                        </p>
                        {item.note && (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.note}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="font-semibold text-sm text-gray-900 dark:text-white shrink-0 ml-3">
                      ₼{(item.price * item.qty).toLocaleString('tr-TR')}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Toplamlar */}
            <div className="pt-4 mt-4 border-t border-gray-200/50 dark:border-white/10 space-y-3 px-2">
              {/* Ara cəm + haqq sətirləri yalnız haqq varsa göstərilir. */}
              {charges.length > 0 && (
                <>
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>{t('pay.subtotal')}</span>
                    <span>₼{money(subtotal)}</span>
                  </div>
                  {charges.map((c) => (
                    <div
                      key={c.id}
                      className="flex justify-between text-sm text-gray-500 dark:text-gray-400"
                    >
                      <span>{c.name}</span>
                      <span>₼{money(Number(c.amount) || 0)}</span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between items-end">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('pay.grandTotal')}
                </span>
                <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  ₼{money(grandTotal)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Alt aksiyonlar */}
      <div className="p-6 bg-gray-50 dark:bg-[#161618] border-t border-gray-200/70 dark:border-white/[0.06] space-y-3">
        {!isEmpty ? (
          <>
            <button
              onClick={onOpenPOS}
              className="w-full py-3.5 bg-[#ff8a00] hover:bg-[#e07a00] text-white rounded-2xl font-semibold shadow-[0_8px_20px_-6px_rgba(255,138,0,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('dock.addOrder')}
            </button>

            <div className={`grid gap-3 ${canTakePayment ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {canTakePayment && (
                <button
                  onClick={onOpenPayment}
                  className="py-3 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 text-gray-900 dark:text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 text-sm border border-transparent hover:border-[#ff8a00]/30 dark:hover:border-[#ff8a00]/40"
                >
                  <CreditCard className="w-4 h-4 text-[#ff8a00]" />
                  {t('dock.payment')}
                </button>
              )}
              <button
                onClick={handlePrintReceipt}
                className="py-3 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 text-gray-900 dark:text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 text-sm border border-transparent hover:border-[#ff8a00]/30 dark:hover:border-[#ff8a00]/40"
              >
                <Printer className="w-4 h-4 text-[#ff8a00]" />
                {t('dock.bill')}
              </button>
            </div>

            {/* "Ayır" (split) və "Digər" düymələri silindi — ikisi də onClick-siz idi
                (heç nə etmirdi) və split funksiyası nə cloud-da, nə pos_local-da var. */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200/50 dark:border-white/10">
              <button
                onClick={() => onShowDetail && onShowDetail(table)}
                className="py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 flex flex-col items-center gap-1.5 transition-colors"
              >
                <QrCode className="w-4 h-4" />
                <span className="text-[10px] font-medium uppercase tracking-wider">QR</span>
              </button>
              <button
                onClick={handleClearTable}
                className="py-2.5 rounded-xl hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex flex-col items-center gap-1.5 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-[10px] font-medium uppercase tracking-wider">{t('dock.clear')}</span>
              </button>
            </div>
          </>
        ) : (
          // Boş masa için sadece QR / detay kısayolu — POS açma butonu yukarıda.
          // ("Digər" düyməsi silindi: onClick-siz idi, heç nə etmirdi.)
          <button
            onClick={() => onShowDetail && onShowDetail(table)}
            className="w-full py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400 flex items-center justify-center gap-2 transition-colors text-sm font-medium"
          >
            <QrCode className="w-4 h-4" />
            {t('dock.detailQr')}
          </button>
        )}
      </div>
      {confirmDialog}
    </motion.div>
  );
}


